import React, { Suspense, useEffect } from 'react';
import { ConditionalWrapper, Scrollable } from '@landr/maestro';
import { LazyLoadModal } from '@landr/mfe';
import { useMilestonesController } from 'controllers/business/Milestones/Milestones.controller';
import PreviewNotification from 'components/PreviewNotification/PreviewNotification';
import FloatingActionsMenu from 'components/FloatingActionsMenu/FloatingActionsMenuConnected';
import Footer from 'components/Footer/Footer';
import AppNavigation from 'components/Navigations/AppNavigation/AppNavigation';
import Fade from 'components/Fade/Fade';
import Loader from 'components/Loader/Loader';
import Creator from 'components/Creator/Creator';
import Onboarding from 'components/Onboarding/Onboarding';
import ScrollToTop from 'components/ScrollToTop/ScrollToTop';
import {
    ErrorBoundaryErrorType,
    useErrorBoundaryContext,
} from 'contexts/ErrorBoundaryContext/ErrorBoundaryContext';
import Modals from 'modals';
import { usePaidDownloadUpsellModal } from 'modals/UpsellModals/PaidDownloadUpsellModalContext';
import { PaidDownloadUpsellModalImport } from 'modals/ModalProviders';
import { trackSamplePageLoadedInPlugin } from 'App.helpers';
import {
    useDisableSpacebarScrolling,
    useResetErrorBoundaryOnLocationChange,
} from 'App.hooks';
import { lazyWithRetries } from 'utils/lazyWithRetries';
import Gradient from 'components/Gradient/Gradient';
import { useGradientContext } from 'controllers/GradientController/GradientController';
import { AppRoutes } from 'routes/AppRoutes';
import { useAppSettings } from 'contexts/AppSettingsContext/AppSettingsContext';
import { useRemoveLocaleFromUrl } from 'hooks/useRemoveLocaleFromUrl/useRemoveLocaleFromUrl';
import useApp from './useApp';

// MODALS AND OVERLAYS
const PluginWelcomeDrawer = lazyWithRetries(
    () => import('./components/PluginWelcomeDrawer'),
);

// ERROR
const PageUnknownError = lazyWithRetries(
    () => import('error/pages/PageUnknownError/PageUnknownError'),
);

const MainContent = ({
    loaderDisplayed,
    onFallbackLoad,
    onFallbackUnload,
    error,
}: {
    loaderDisplayed: boolean;
    onFallbackLoad: () => void;
    onFallbackUnload: () => void;
    error: ErrorBoundaryErrorType | undefined;
}) => {
    const { color, opacity } = useGradientContext();

    return (
        <Fade in={!loaderDisplayed} exit={false}>
            {(): JSX.Element => (
                <Suspense
                    fallback={
                        <Loader
                            onLoad={onFallbackLoad}
                            onUnload={onFallbackUnload}
                        />
                    }
                >
                    <Gradient color={color} opacity={opacity} />
                    {error?.variant === 'page' && (
                        <PageUnknownError status={error?.status} />
                    )}
                    {error?.variant !== 'page' && <AppRoutes />}
                    {error?.variant !== 'page' && <Footer />}
                </Suspense>
            )}
        </Fade>
    );
};

const OverlayContent = ({
    isAuthenticated,
    isDAWPlugin,
}: {
    isAuthenticated: boolean;
    isDAWPlugin: boolean;
}) => {
    const isPluginWelcomeDrawerAllowed = isDAWPlugin && isAuthenticated;
    const isOnboardingAllowed = !isDAWPlugin || isAuthenticated;

    return (
        <Suspense fallback={null}>
            <Creator />
            <FloatingActionsMenu />
            <PreviewNotification />
            {isPluginWelcomeDrawerAllowed && <PluginWelcomeDrawer />}
            {isOnboardingAllowed && <Onboarding />}
            <Modals />
            <ScrollToTop />
        </Suspense>
    );
};

const App: React.FunctionComponent = () => {
    const {
        isDAWPlugin,
        isAuthenticated,
        loaderDisplayed,
        onFallbackLoad,
        onFallbackUnload,
        error,
    } = useApp();

    useRemoveLocaleFromUrl();

    const { isExtendableHeight } = useAppSettings();

    const { perform, get } = useMilestonesController();

    const { error: errorBoundaryError, setError } = useErrorBoundaryContext();

    useResetErrorBoundaryOnLocationChange(errorBoundaryError, setError);
    useDisableSpacebarScrolling();

    useEffect(() => {
        trackSamplePageLoadedInPlugin(
            isDAWPlugin,
            get('SamplesPageLoadedInPlugin'),
            perform,
        );
    }, [isDAWPlugin, get, perform]);

    return (
        <Suspense fallback={null}>
            <LazyLoadModal
                importModule={PaidDownloadUpsellModalImport}
                useModal={usePaidDownloadUpsellModal}
            />

            <ConditionalWrapper
                condition={!isExtendableHeight}
                wrapper={(children) => (
                    <Scrollable id="samples-app-scrollable" height="100vh">
                        {children}
                    </Scrollable>
                )}
            >
                <AppNavigation />
                <MainContent
                    loaderDisplayed={loaderDisplayed}
                    onFallbackLoad={onFallbackLoad}
                    onFallbackUnload={onFallbackUnload}
                    error={error}
                />
                <OverlayContent
                    isAuthenticated={isAuthenticated}
                    isDAWPlugin={isDAWPlugin}
                />
            </ConditionalWrapper>
        </Suspense>
    );
};

export default App;
